import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import AppLayout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Clients from "@/pages/Clients";
import Projects from "@/pages/Projects";
import Tasks from "@/pages/Tasks";
import Inbox from "@/pages/Inbox";
import Invoices from "@/pages/Invoices";
import Reports from "@/pages/Reports";
import Goals from "@/pages/Goals";
import Referrals from "@/pages/Referrals";
import Notes from "@/pages/Notes";
import Piano from "@/pages/Piano";
import Quotes from "@/pages/Quotes";
import PublicQuote from "@/pages/PublicQuote";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import Admin from "@/pages/Admin";
import Settings from "@/pages/Settings";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Terms from "@/pages/Terms";
import CookieBanner from "@/components/CookieBanner";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-zen-ink2">Caricamento…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-zen-ink2">Caricamento…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <Navigate to="/dashboard" replace />;
  return children;
}

function Router() {
  const location = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);
  // Handle Emergent Google Auth callback — synchronous check to avoid race conditions
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/q/:token" element={<PublicQuote />} />
      <Route element={<Protected><AppLayout /></Protected>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/clienti" element={<Clients />} />
        <Route path="/progetti" element={<Projects />} />
        <Route path="/attivita" element={<Tasks />} />
        <Route path="/inbox" element={<Inbox />} />
        <Route path="/preventivi" element={<Quotes />} />
        <Route path="/fatture" element={<Invoices />} />
        <Route path="/report" element={<Reports />} />
        <Route path="/obiettivi" element={<Goals />} />
        <Route path="/invita" element={<Referrals />} />
        <Route path="/appunti" element={<Notes />} />
        <Route path="/piano" element={<Piano />} />
        <Route path="/piano/success" element={<Piano />} />
        <Route path="/impostazioni" element={<Settings />} />
        <Route path="/admin" element={<AdminOnly><Admin /></AdminOnly>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <Router />
          <CookieBanner />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}
